import { useEffect } from 'react';
import { TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import { HEADER_TYPE, extractTokens } from '../state';
import { checkHeaderTextFormat } from '../validate';
import { friendlyError } from './errorMessages';

// Preserves the value of a row whose token is still present; drops rows for tokens no longer
// typed; adds a fresh {name, value:'', error:''} row for a newly-typed token. Same
// filter-then-append-missing shape as gs-ui-templates Body/index.tsx's own getVariables.
function mergeVariables(existingRows, tokens) {
  const byName = new Map(existingRows.map((row) => [row.name, row]));
  return tokens.map((token) => byName.get(token) || { name: token, value: '', error: '' });
}

function sameTokens(rows, tokens) {
  return rows.length === tokens.length && rows.every((row, index) => row.name === tokens[index]);
}

// Shared row renderer for HeaderSampleFields/BodySampleFields below. Live per-keystroke
// validation only applies to HEADER variables — checkHeaderTextFormat on change, with row.error
// winning over the submit-time aggregate, the same live-beats-submit precedence Header.js's own
// header-text field uses (headerTextDisplayError). Body variables get no format check on either
// side: error stays '' unconditionally on every keystroke, same as before this task.
function renderVariableRow({
  row, key, path, errorKey,
}, isHeaderVariable, { dispatch, errors, disabled }) {
  const submitError = errors[errorKey];
  const displayError = isHeaderVariable ? (row.error || submitError) : submitError;
  return (
    <div className="br-tpl-form-variable-row" key={key}>
      <TextField
        fullWidth
        size="small"
        value={row.value}
        placeholder="Enter sample variable value"
        disabled={disabled}
        error={Boolean(displayError)}
        helperText={friendlyError(displayError)}
        autoComplete="off"
        onChange={(event) => dispatch({
          type: 'merge',
          path,
          value: {
            value: event.target.value,
            error: isHeaderVariable ? checkHeaderTextFormat(event.target.value) : '',
          },
        })}
        InputProps={{
          startAdornment: <Typography className="br-tpl-form-variable-token">{row.name}</Typography>,
        }}
        inputProps={{ 'data-testid': `bloomreach-templates-form-variable-${path}` }}
      />
    </div>
  );
}

// B4: sample-value fields for the header — rendered by Header.js inside its own card, below the
// header text input, matching the console's own placement (gs-ui-templates' HeaderText.tsx mounts
// its own <Variables field="HEADER" .../> there rather than a separate card). One row per {{n}}
// token typed in the header text; the token bookkeeping itself stays in VariablesSection below,
// this only reads the resulting rows.
export function HeaderSampleFields({
  state, dispatch, errors, disabled,
}) {
  const rows = state.header.headerText.variable.map((row, index) => ({
    row,
    key: `header-${row.name}`,
    path: `header.headerText.variable.${index}`,
    errorKey: `header.headerText.variable.${index}.value`,
  }));
  if (rows.length === 0) return null;

  return (
    <>
      <Typography variant="subtitle1" data-testid="bloomreach-templates-form-variables-header-heading">
        Define sample variable value for header
      </Typography>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Samples are a way of providing an example of possible data for your template. This helps
        us during the review and approval process, so we can understand what kind of message you
        plan to send.
      </Typography>
      <div className="br-tpl-form-variable-rows">
        {rows.map((entry) => renderVariableRow(entry, true, { dispatch, errors, disabled }))}
      </div>
    </>
  );
}

// B4: sample-value fields for the body — rendered by Body.js inside its own card after an <hr>,
// same console parity as HeaderSampleFields above (Body/index.tsx mounts its own
// <Variables field="BODY" .../>). One row per {{n}} token typed in the body text.
export function BodySampleFields({
  state, dispatch, errors, disabled,
}) {
  const rows = state.body.variable.map((row, index) => ({
    row,
    key: `body-${row.name}`,
    path: `body.variable.${index}`,
    errorKey: `body.variable.${index}.value`,
  }));
  if (rows.length === 0) return null;

  return (
    <>
      <Typography variant="subtitle1" data-testid="bloomreach-templates-form-variables-body-heading">
        Define sample variable value for body
      </Typography>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Samples are a way of providing an example of possible data for your template. This helps
        us during the review and approval process, so we can understand what kind of message you
        plan to send.
      </Typography>
      <div className="br-tpl-form-variable-rows">
        {rows.map((entry) => renderVariableRow(entry, false, { dispatch, errors, disabled }))}
      </div>
    </>
  );
}

// 6. Variables — no longer renders a card of its own. B4 moved the header samples into Header.js
// and the body samples into Body.js (HeaderSampleFields/BodySampleFields above); this section
// stays mounted (see sections/index.js) purely to keep running the two token-sync effects below,
// which derive header.headerText.variable / body.variable from the raw text on every keystroke —
// both Header.js and Body.js read those derived arrays, they don't recompute them.
const VariablesSection = ({ state, dispatch }) => {
  const headerText = state.header.value === HEADER_TYPE.TEXT ? state.header.headerText.value : '';
  const bodyText = state.body.value;

  useEffect(() => {
    const tokens = extractTokens(headerText);
    if (!sameTokens(state.header.headerText.variable, tokens)) {
      dispatch({
        type: 'merge',
        path: 'header.headerText',
        value: { variable: mergeVariables(state.header.headerText.variable, tokens) },
      });
    }
    // Deps are the raw text only — re-deriving off `.variable` itself would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerText]);

  useEffect(() => {
    const tokens = extractTokens(bodyText);
    if (!sameTokens(state.body.variable, tokens)) {
      dispatch({ type: 'merge', path: 'body', value: { variable: mergeVariables(state.body.variable, tokens) } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyText]);

  return null;
};

export default VariablesSection;
